import { Stack } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { t } from 'i18next'
import { Control } from 'react-hook-form'
import CheckboxInput from '../../components/Form/Inputs/CheckboxInput'
import DateInput from '../../components/Form/Inputs/DateInput'
import SelectInput, { Option } from '../../components/Form/Inputs/SelectInput'
import TextInput from '../../components/Form/Inputs/TextInput'
import i18n from '../../config/i18n'
import { environment } from '../../enviroments/enviroment'
import { UserFormValues } from '../../store/types'

type ProfileViewFormProps = {
  isMyProfile: boolean
  isNewUser: boolean
  isManagement: boolean
  control: Control<UserFormValues, any>
}

const getDomainArray = () => {
  let domainsString = environment.allowedDomains
  let domains: string[] = []
  if (domainsString) domains = domainsString.split(',')

  return domains
}

const validateDomains = (email: string) => {
  let domains: string[] = getDomainArray()
  let isValid = false
  domains.forEach(d => {
    if (email.endsWith(d)) isValid = true
  })
  return isValid
}

const ProfileViewForm = ({ isMyProfile, isNewUser, isManagement, control }: ProfileViewFormProps) => {
  const phoneUtil = PhoneNumberUtil.getInstance()

  const employeeStatusOptions: Option[] = [
    { value: 'ACTIVE', label: t('page.usersProfile.form.statuses.active') },
    { value: 'BLOCKED', label: t('page.usersProfile.form.statuses.blocked') },
    { value: 'DELETED', label: t('page.usersProfile.form.statuses.deleted') },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
      <Stack gap={2} minWidth={210}>
        <TextInput
          name="firstName"
          control={control}
          required
          label={t('page.usersProfile.form.fields.firstName')}
          placeholder={t('page.usersProfile.form.fields.firstNamePlaceholder')}
          rules={{
            required: t('page.usersProfile.form.errors.firstNameRequired'),
            maxLength: { value: 50, message: t('page.usersProfile.form.errors.firstNameMaxLengh') },
            pattern: {
              value:
                /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžśŚÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+(?:[ ,.'-][a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžśŚÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+)*$/,
              message: t('page.usersProfile.form.errors.namePattern'),
            },
          }}
          maxLength={50}
        />

        <TextInput
          name="lastName"
          control={control}
          required
          label={t('page.usersProfile.form.fields.lastName')}
          placeholder={t('page.usersProfile.form.fields.lastNamePlaceholder')}
          rules={{
            required: t('page.usersProfile.form.errors.lastNameRequired'),
            maxLength: { value: 50, message: t('page.usersProfile.form.errors.lastNameMaxLengh') },
            pattern: {
              value:
                /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžśŚÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+(?:[ ,.'-][a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžśŚÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð]+)*$/,
              message: t('page.usersProfile.form.errors.namePattern'),
            },
          }}
          maxLength={50}
        />

        {isNewUser && (
          <TextInput
            name="email"
            control={control}
            required
            label={t('page.usersProfile.form.fields.email')}
            placeholder={t('page.usersProfile.form.fields.emailPlaceholder')}
            rules={{
              required: t('page.usersProfile.form.errors.emailRequired'),
              validate: {
                acceptedDomain: value => {
                  getDomainArray()
                  return validateDomains(value?.toString()) || t('page.usersProfile.form.errors.domainNotAccepted')
                },
              },
            }}
          />
        )}

        <TextInput
          name="phoneNumber"
          control={control}
          label={t('page.usersProfile.form.fields.phone')}
          placeholder={t('page.usersProfile.form.fields.phonePlaceholder')}
          rules={{
            maxLength: {
              value: 12,
              message: t('page.usersProfile.form.errors.phoneLength'),
            },
            minLength: {
              value: 0,
              message: t('page.usersProfile.form.errors.phoneLength'),
            },
            validate: {
              whitespace: value => {
                if (typeof value === 'string' && /\s/.test(value)) {
                  return t('page.usersProfile.form.errors.whitespaceNotAllowed')
                } else return true
              },
              valid: value => {
                if (value === null || value === '' || value === undefined) return true
                if (typeof value === 'string') {
                  try {
                    const parsedPhoneNumber = phoneUtil.parse(value, 'PL')
                    return phoneUtil.isValidNumber(parsedPhoneNumber)
                      ? true
                      : t('page.usersProfile.form.errors.phoneInvalid')
                  } catch (e) {
                    return t('page.usersProfile.form.errors.phoneInvalid')
                  }
                }
                return t('page.usersProfile.form.errors.phoneInvalid')
              },
            },
          }}
        />

        <DateInput
          name={'birthDate'}
          control={control}
          label={t('page.usersProfile.form.fields.birthDate')}
          rules={{
            validate: {
              valid: date => {
                if (date === null || date === undefined) return true
                return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
              },
              min: date => {
                if (date === null || date === undefined) return true
                return (
                  !dayjs(date.toString()).isBefore(dayjs('1900-01-01')) ||
                  t('page.usersProfile.form.errors.birthDatePastDate')
                )
              },
              max: date => {
                if (date === null || date === undefined) return true
                if (dayjs.isDayjs(date) && !date.isValid()) return false
                return (
                  dayjs(date.toString()).utc(true).isBefore(dayjs()) ||
                  t('page.usersProfile.form.errors.birthDateFutureDate')
                )
              },
            },
          }}
        />

        {(isManagement || isNewUser) && (
          <SelectInput
            options={employeeStatusOptions}
            name={'employeeStatus'}
            control={control}
            label={'Status'}
            disabled={isMyProfile}
          />
        )}

        <CheckboxInput name="student" control={control} label={'Student'} />
      </Stack>
    </LocalizationProvider>
  )
}

export default ProfileViewForm
